import React, { useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { ReceiptPercentIcon } from '@heroicons/react/24/outline';
import storeApi from '../../../../services/storeApi';
import DialogShell from '../components/dialog-shell';
import {
  SectionHeading,
  FieldLabel,
  FieldError,
  primaryButtonClass,
  secondaryButtonClass,
  inputBaseClass,
} from '../components/settings-ui';

const TaxesTab = ({ storeDetail, checkUpdate }) => {
  const [form, setForm] = useState({ ...storeDetail });
  const [errors, setErrors] = useState({ tax: false });
  const [loading, setLoading] = useState({ upsert: false });
  const [dialogs, setDialogs] = useState({ upsert: false });

  const onFormChange = (val, key) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const onClickEdit = () => {
    setForm({ ...storeDetail });
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onCloseUpsert = () => {
    setErrors({ tax: false });
    setDialogs((prev) => ({ ...prev, upsert: false }));
  };

  const onFormSubmit = async () => {
    if (!form.tax) {
      setErrors({ tax: true });
      return;
    }
    setErrors({ tax: false });

    try {
      setLoading({ upsert: true });
      const body = { ...form, websiteclosedat: form.websiteclosedat || '' };
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      const res = await storeApi.upsert(body, config);
      if (res.status === 200) {
        checkUpdate(true);
      }
    } catch (error) {
      checkUpdate(false, error?.response?.data);
    } finally {
      setLoading({ upsert: false });
      setDialogs((prev) => ({ ...prev, upsert: false }));
    }
  };

  return (
    <div className="space-y-6">
      <SectionHeading
        title="Taxes"
        description="Applied to every order at checkout. Enter a percentage with up to three decimals."
      />

      <div className="rounded-md border border-slate-200 bg-gradient-to-br from-white to-slate-50/70 p-5 shadow-sm dark:border-slate-700 dark:from-slate-900/60 dark:to-slate-900/20">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              <ReceiptPercentIcon className="h-6 w-6" aria-hidden="true" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                Sales tax rate
              </p>
              <p className="mt-1 flex items-baseline gap-2">
                <span className="text-3xl font-bold tabular-nums text-slate-900 dark:text-slate-100">
                  {Number(storeDetail.tax || 0).toFixed(3)}
                </span>
                <span className="text-lg font-semibold text-slate-400 dark:text-slate-500">
                  %
                </span>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClickEdit}
            className={primaryButtonClass + ' w-full sm:w-auto'}
          >
            Update rate
          </button>
        </div>
      </div>

      <DialogShell
        show={dialogs.upsert}
        onClose={onCloseUpsert}
        title="Update sales tax"
        description="Applied to the subtotal of every order placed at this store."
        size="md"
        footer={
          <>
            <button
              type="button"
              onClick={onCloseUpsert}
              className={secondaryButtonClass}
              disabled={loading.upsert}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onFormSubmit}
              className={primaryButtonClass}
              disabled={loading.upsert}
            >
              {loading.upsert ? 'Saving…' : 'Save changes'}
            </button>
          </>
        }
      >
        <div className="space-y-2">
          <FieldLabel htmlFor="tax" required>
            Sales tax (%)
          </FieldLabel>
          <CurrencyInput
            id="tax"
            name="tax"
            placeholder="0.000"
            className={inputBaseClass + ' tabular-nums'}
            prefix="%"
            value={form.tax}
            decimalSeparator="."
            groupSeparator=","
            decimalScale={3}
            step={1}
            defaultValue={0}
            decimalsLimit={3}
            onValueChange={(value) => onFormChange(value, 'tax')}
          />
          <FieldError
            message={errors.tax ? 'Please enter a tax rate.' : null}
          />
        </div>
      </DialogShell>
    </div>
  );
};

export default TaxesTab;
